import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Popover,
  Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useIsMobile } from '@uplift/hooks';
import { Button, ScrollableView, ScrollableViewProps } from '@uplift/ui-v2';
import { Fragment, useRef, useState } from 'react';
import { CloseOutlined, ExpandMore } from '@mui/icons-material';

export type FiltersChipMenuProps = {
  title: React.ReactNode;
  highlighted?: boolean;
  contentTitle?: string;
  onClear?: () => void;
  onApply: () => void;
  scrollableViewProps?: ScrollableViewProps;
  disabled?: boolean;
};

export const FiltersChipMenu: React.FC<FiltersChipMenuProps> = props => {
  const anchor = useRef<HTMLDivElement>(null);
  const [ open, setOpen ] = useState(false);
  const isMobile = useIsMobile('sm');
  const { classes, cx } = useCustomChipMenuStyles();

  const onApply = () => {
    setOpen(false);
    props.onApply();
  };

  const actions = (
    <div className={cx(classes.actions, !props.onClear && classes.actionsEnd)}>
      {props.onClear && (<Button size="small" onClick={props.onClear}>Clear</Button>)}
      <Button variant="contained" color="secondary" size="small" onClick={onApply}>Apply</Button>
    </div>
  );

  return (
    <Fragment>
      <Chip
        variant="outlined"
        color="secondary"
        disabled={props.disabled}
        label={props.title}
        onClick={() => setOpen(true)}
        ref={anchor}
        classes={{
          root: cx(classes.chipRoot, {
            [classes.chipRootHighlighted]: props.highlighted,
            [classes.chipRootOpen]: open,
          }),
          label: cx(classes.chipLabel, {
            [classes.chipLabelHighlighted]: props.highlighted,
          }),
        }}
        icon={<ExpandMore />}
      />
      {!isMobile && (
        <Popover
          open={open}
          anchorEl={anchor?.current}
          onClose={() => setOpen(false)}
          classes={{ root: classes.popoverRoot, paper: classes.popoverPaper }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
        >
          <ScrollableView height="400px" {...props.scrollableViewProps}>
            {props.children}
          </ScrollableView>
          {actions}
        </Popover>
      )}
      {isMobile && (
        <Dialog fullScreen open={open}>
          <Box className={classes.dialogHeader}>
            <Typography variant="h3">{props.contentTitle}</Typography>
            <IconButton aria-label="close" onClick={() => setOpen(false)} size="small">
              <CloseOutlined />
            </IconButton>
          </Box>
          <DialogContent className={classes.dialogContent}>
            {props.children}
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            {actions}
          </DialogActions>
        </Dialog>
      )}
    </Fragment>
  );
};

const useCustomChipMenuStyles = makeStyles({ name: 'CustomChipMenu' })(theme => ({
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1,
    [theme.breakpoints.up('sm')]: {
      borderTop: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(2),
    },
    backgroundColor: theme.palette.background.paper,
    zIndex: 2
  },
  actionsEnd: {
    justifyContent: 'flex-end'
  },
  chipRoot: {
    backgroundColor: theme.palette.background.paper,
    borderColor: theme.palette.divider,
    flexDirection: 'row-reverse',

    '&:hover': {
      borderColor: theme.palette.secondary.border,
    },

    '& .MuiChip-icon': {
      marginLeft: '0 !important',
      marginRight: theme.spacing(0.5),
      fontSize: '18px',
      color: theme.palette.text.secondary,
    }
  },
  chipRootHighlighted: {
    borderColor: theme.palette.secondary.border,
  },
  chipRootOpen: {
    backgroundColor: theme.palette.secondary.background,
    borderColor: theme.palette.secondary.border,
  },
  chipLabel: {
    color: theme.palette.text.primary,
    paddingRight: theme.spacing(0.25),
  },
  chipLabelHighlighted: {
    fontWeight: 500,
  },
  content: {
    padding: 0,
    height: '100%',
  },
  popoverRoot: {
    top: '8px !important',
  },
  popoverPaper: {
    maxWidth: '400px',
    width: '100%',
  },
  dialogHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  dialogContent: {
    padding: 0,
  },
  dialogActions: {
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
  }
}));
